import React from "react";
import { fmtDate } from "../lib/format";
import {
  AdditionalServicesTable,
  CallbackWidget,
  CoiBadge,
  DestCard,
  IncludedServices,
  LiabilityCard,
  PaymentsCard,
  ReadField,
  S2Field,
  SectionLabel,
  YesNoBadge,
  ZohoBadgeVal,
  ZohoLabel,
  makeS2Setter,
  NoteTextarea,
} from "./fields";

/** A Zoho address with its apartment / unit underneath. */
const AddressField = ({ label, address, apt }) => (
  <div className="field">
    <label>{label}</label>
    <div className="val">{address}</div>
    <label style={{ marginTop: 8 }}>Apartment / unit</label>
    <div className="val">{apt || "—"}</div>
  </div>
);

export const TabIntroCall = ({ job, isLD, actions }) => {
  const s2 = (k) => (job.s2Notes && job.s2Notes[k]) || "";
  const set = makeS2Setter(job, actions.setS2Note);

  return (
    <>
      <CallbackWidget
        job={job}
        onSave={(d, t) => actions.saveCallback(job.id, d, t)}
      />

      <SectionLabel icon="activity">Client Details</SectionLabel>
      <div className="field-row">
        <AddressField label="Origin address" address={job.moveFrom} apt={job.originApt} />
        <AddressField label="Destination address" address={job.moveTo} apt={job.destApt} />
      </div>
      <div className="field-row">
        <S2Field job={job} fieldKey="originNote" label="Origin notes" onChange={set} />
        <S2Field job={job} fieldKey="destNote" label="Destination notes" onChange={set} />
      </div>
      <S2Field job={job} fieldKey="contactless" label="Contactless move?" onChange={set} />

      <SectionLabel icon="truck">Logistics</SectionLabel>
      <div className="field-row">
        <ReadField label="Move date (Zoho)">{fmtDate(job.moveDate)}</ReadField>
        <div className="field">
          <label>
            <ZohoLabel text="Packing date (Zoho)" included={!!job.packingDate} />
          </label>
          <div className="val">{fmtDate(job.packingDate)}</div>
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>
            <ZohoLabel text="Start time (Zoho)" included={!!job.startTime} />
          </label>
          <div className="val">{job.startTime || "—"}</div>
        </div>
        <div className="field">
          <label>
            <ZohoLabel text="Unpacking date (Zoho)" included={!!job.unpackingDate} />
          </label>
          <div className="val">{fmtDate(job.unpackingDate)}</div>
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>
            <ZohoLabel text="Total cubic feet (Zoho)" included={!!job.totalCubicFeet} />
          </label>
          <div className="val">{job.totalCubicFeet || "—"}</div>
        </div>
        <div className="field">
          <label>
            <ZohoLabel text="Loading date (Zoho)" included={!!job.loadingDate} />
          </label>
          <div className="val">{fmtDate(job.loadingDate)}</div>
        </div>
      </div>
      <div className="field">
        <label>Disposal service</label>
        <div className="val">
          <ZohoBadgeVal included={job.disposalIncluded} />
        </div>
      </div>
      <S2Field job={job} fieldKey="logisticsNotes" label="Notes" onChange={set} />

      <SectionLabel icon="activity">Pick Up</SectionLabel>
      {isLD ? (
        <DestCard
          icon="home"
          label="Pickup Address"
          address={job.moveFrom}
          stats={[
            ["Move date", fmtDate(job.moveDate)],
            ["Packing date", fmtDate(job.packingDate)],
            ["Pickup window", s2("pickupWindow") || "—"],
          ]}
        />
      ) : null}
      <div className="field-row">
        <div className="field">
          <label>Stairs</label>
          <div className="val">
            <YesNoBadge value={job.stairsAtPickup} />
          </div>
        </div>
        <div className="field">
          <label>Elevator</label>
          <div className="val">
            <YesNoBadge value={job.elevatorAtPickup} />
          </div>
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>Flights of stairs</label>
          <div className="val" style={{ marginBottom: 6 }}>
            {job.stairsAtPickup ? job.stairsFlights || "—" : "—"}
          </div>
          <NoteTextarea job={job} fieldKey="stairsFlightsNote" onChange={set} />
        </div>
        <div className="field">
          <label>Elevator restriction</label>
          <div className="val" style={{ marginBottom: 6 }}>
            {job.elevatorRestriction || "—"}
          </div>
          <NoteTextarea job={job} fieldKey="elevatorRestrictionNote" onChange={set} />
        </div>
      </div>
      <div className="field-row">
        <S2Field job={job} fieldKey="pickupMasonite" label="Masonite required" onChange={set} />
        <div className="field">
          <label>COI completed</label>
          <div className="val">
            <CoiBadge completed={job.coiCompletedPickup} required={job.coiRequiredPickup !== false} />
          </div>
        </div>
      </div>
      <div className="field-row">
        <S2Field job={job} fieldKey="longCarryPickup" label="Long carry at pickup" onChange={set} />
        <S2Field job={job} fieldKey="pickupBuildingReq" label="Building requirements" onChange={set} />
      </div>
      <S2Field job={job} fieldKey="pickupNotes" label="Pickup note" onChange={set} />

      <SectionLabel icon="check">Special Services</SectionLabel>
      <IncludedServices job={job} />
      <AdditionalServicesTable items={job.additionalServiceItems} />
      <S2Field job={job} fieldKey="specialServicesNotes" label="Special Service Notes" onChange={set} />

      <SectionLabel icon="activity">Delivery</SectionLabel>
      {isLD ? (
        <DestCard
          icon="truck"
          label="Delivery Address"
          address={job.moveTo}
          stats={[
            ["Move date", fmtDate(job.moveDate)],
            ["First ready date", fmtDate(job.deliveryWindowStart)],
            [
              "Delivery window",
              `${fmtDate(job.deliveryWindowStart)} – ${fmtDate(job.deliveryWindowEnd)}`,
            ],
          ]}
        />
      ) : null}
      <div className="field-row">
        <div className="field">
          <label>Stairs</label>
          <div className="val">
            <YesNoBadge value={job.stairsAtDelivery} />
          </div>
        </div>
        <div className="field">
          <label>Elevator</label>
          <div className="val">
            <YesNoBadge value={job.elevatorAtDelivery} />
          </div>
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>Flights of stairs</label>
          <div className="val" style={{ marginBottom: 6 }}>
            {job.stairsAtDelivery ? job.stairsFlightsDelivery || "—" : "—"}
          </div>
          <NoteTextarea job={job} fieldKey="stairsFlightsNoteDelivery" onChange={set} />
        </div>
        <div className="field">
          <label>Elevator restriction</label>
          <div className="val" style={{ marginBottom: 6 }}>
            {job.elevatorRestrictionDelivery || "—"}
          </div>
          <NoteTextarea job={job} fieldKey="elevatorRestrictionNoteDelivery" onChange={set} />
        </div>
      </div>
      <div className="field-row">
        <S2Field job={job} fieldKey="deliveryMasonite" label="Masonite required" onChange={set} />
        <div className="field">
          <label>COI completed</label>
          <div className="val">
            <CoiBadge completed={job.coiCompletedDelivery} required={job.coiRequiredDelivery !== false} />
          </div>
        </div>
      </div>
      <div className="field-row">
        <S2Field job={job} fieldKey="longCarryDelivery" label="Long carry at delivery" onChange={set} />
        <S2Field job={job} fieldKey="deliveryBuildingReq" label="Building requirements" onChange={set} />
      </div>
      <S2Field job={job} fieldKey="deliveryNotes" label="Delivery notes" onChange={set} />

      <SectionLabel icon="alert">Liability</SectionLabel>
      <LiabilityCard job={job} set={set} actions={actions} />

      <SectionLabel icon="check">Payments</SectionLabel>
      <PaymentsCard job={job} set={set} actions={actions} />

      {job.introCallDone ? (
        <>
          <SectionLabel icon="reply">
            Confirmation Follow-ups (not covered in intro call)
          </SectionLabel>
          <div className="field-row">
            <S2Field job={job} fieldKey="unpacking" label="Unpacking" onChange={set} />
            <S2Field job={job} fieldKey="coisArranged" label="Do you have COIs arranged?" onChange={set} />
          </div>
          <div className="field-row">
            <S2Field
              job={job}
              fieldKey="salesRepNeeded"
              label="Does client need their sales rep to follow up?"
              onChange={set}
            />
            <S2Field job={job} fieldKey="scopeChanges" label="Any scope changes?" onChange={set} />
          </div>
        </>
      ) : null}

      <SectionLabel icon="note">Feedback note</SectionLabel>
      <NoteTextarea job={job} fieldKey="introFeedback" hint="Feedback from this call..." onChange={set} rows={3} />
      {job.introCallDone ? (
        <div className="toolbar-row">
          <span className="badge resolved">
            <span className="bdot" />
            Intro call done
          </span>
        </div>
      ) : null}
    </>
  );
};
